import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { plantsAPI } from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import PlantCard from '../components/PlantCard';
import ThemeToggle from '../components/ThemeToggle';
import ReportBugButton from '../components/ReportBugButton';
import AIPlantLens from '../components/AIPlantLens';
import './Dashboard.css';

function Dashboard({ user, onLogout }) {
  const [plants, setPlants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSpecies, setFilterSpecies] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isAILensOpen, setIsAILensOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plantsRes, statsRes] = await Promise.all([
        plantsAPI.getAll(),
        plantsAPI.getDashboardStats()
      ]);
      setPlants(plantsRes.data);
      setStats(statsRes.data);
      setError('');
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique species from plants
  const getUniqueSpecies = () => {
    const species = plants
      .map(plant => plant.species)
      .filter(species => species && species.trim() !== '');
    return [...new Set(species)].sort();
  };

  // Filter and sort plants
  const getFilteredAndSortedPlants = () => {
    let filtered = [...plants];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(plant => 
        plant.name.toLowerCase().includes(query) ||
        (plant.species && plant.species.toLowerCase().includes(query)) ||
        (plant.tags && plant.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Apply category filter
    if (filterCategory) {
      if (filterCategory === 'Favorite') {
        filtered = filtered.filter(plant => plant.favorite);
      } else {
        filtered = filtered.filter(plant => plant.category === filterCategory);
      }
    }

    // Apply species filter
    if (filterSpecies) {
      filtered = filtered.filter(plant => plant.species === filterSpecies);
    }

    // Apply sort
    if (sortBy === 'name-asc') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'name-desc') {
      filtered.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === 'age-oldest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.acquisitionDate || a.createdAt);
        const dateB = new Date(b.acquisitionDate || b.createdAt);
        return dateA - dateB;
      });
    } else if (sortBy === 'age-newest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.acquisitionDate || a.createdAt);
        const dateB = new Date(b.acquisitionDate || b.createdAt);
        return dateB - dateA;
      });
    }

    return filtered;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterCategory('');
    setFilterSpecies('');
    setSortBy('');
  };

  const handleFavoriteCategoryClick = () => {
    setFilterCategory('Favorite');
    setShowFilters(true);
  };

  const handleNewestPlantClick = () => {
    if (stats?.newestPlant?.id) {
      navigate(`/plants/${stats.newestPlant.id}`);
    }
  };

  const handleOldestPlantClick = () => {
    if (stats?.oldestPlant?.id) {
      navigate(`/plants/${stats.oldestPlant.id}`);
    }
  };

  const handleAddToInventory = async (plantData) => {
    navigate('/plants/new', { state: { identifiedPlant: plantData } });
  };

  const handleAddToWishlist = async (plantData) => {
    navigate('/plants/new', { state: { identifiedPlant: { ...plantData, wishlist: true } } });
  };

  const displayedPlants = getFilteredAndSortedPlants();

  if (loading) {
    return <div className="loading">Loading your plants...</div>;
  }

  return (
    <div className="dashboard">
      <ReportBugButton />
      <header className="dashboard-header">
        <div className="header-content">
          <h1>🌿 My Plants</h1>
          <div className="header-actions">
            <ThemeToggle />
            <button onClick={onLogout} className="btn-logout">Logout</button>
          </div>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <div className="dashboard-content">
        {stats && (
          <div className="stats-section">
            <h2>Summary</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{stats.totalPlants}</div>
                <div className="stat-label">Total Plants</div>
              </div>
              <div 
                className="stat-card stat-card-clickable" 
                onClick={handleFavoriteCategoryClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleFavoriteCategoryClick();
                  }
                }}
              >
                <div className="stat-value">{stats.favoriteCategory}</div>
                <div className="stat-label">Favorite Category</div>
              </div>
              {stats.newestPlant && (
                <div 
                  className="stat-card stat-card-clickable"
                  onClick={handleNewestPlantClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNewestPlantClick();
                    }
                  }}
                >
                  <div className="stat-value">{stats.newestPlant.name}</div>
                  <div className="stat-label">Newest Plant</div>
                  <div className="stat-date">
                    {formatDate(stats.newestPlant.date)}
                  </div>
                </div>
              )}
              {stats.oldestPlant && (
                <div 
                  className="stat-card stat-card-clickable"
                  onClick={handleOldestPlantClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOldestPlantClick();
                    }
                  }}
                >
                  <div className="stat-value">{stats.oldestPlant.name}</div>
                  <div className="stat-label">Oldest Plant</div>
                  <div className="stat-date">
                    {formatDate(stats.oldestPlant.date)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="plants-section">
          <div className="section-header">
            <h2>Your Plants</h2>
            <div className="header-buttons">
              <button 
                onClick={() => setShowFilters(!showFilters)} 
                className="btn-filter-toggle"
                aria-label={showFilters ? "Hide filters" : "Show filters"}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="white" 
                  width="20" 
                  height="20"
                >
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
                </svg>
              </button>
              <button 
                onClick={() => setIsAILensOpen(true)} 
                className="btn btn-ai-lens"
                title="Identify plant with AI"
              >
                📷 AI Lens
              </button>
              <button 
                onClick={() => navigate('/plants/new')} 
                className="btn btn-add"
              >
                + Add Plant
              </button>
            </div>
          </div>

          {plants.length === 0 ? (
            <div className="empty-state">
              <p>🌱 No plants yet!</p>
              <p>Start building your collection by adding your first plant.</p>
              <button 
                onClick={() => navigate('/plants/new')} 
                className="btn btn-primary"
              >
                Add Your First Plant
              </button>
            </div>
          ) : (
            <>
              {showFilters && (
                <>
                  <div className="search-bar">
                    <input
                      type="text"
                      placeholder="🔍 Search plants by name, species, or tags..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                  </div>

                  <div className="filter-sort-controls">
                <div className="filter-controls">
                  <div className="control-group">
                    <label htmlFor="category-filter">Filter by Category:</label>
                    <select
                      id="category-filter"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="filter-select"
                    >
                      <option value="">All Categories</option>
                      <option value="Favorite">Favorite</option>
                      <option value="Succulent">Succulent</option>
                      <option value="Houseplant">Houseplant</option>
                      <option value="Herb">Herb</option>
                      <option value="Outdoor">Outdoor</option>
                      <option value="Flower">Flower</option>
                      <option value="Tree">Tree</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="control-group">
                    <label htmlFor="species-filter">Filter by Species:</label>
                    <select
                      id="species-filter"
                      value={filterSpecies}
                      onChange={(e) => setFilterSpecies(e.target.value)}
                      className="filter-select"
                      disabled={getUniqueSpecies().length === 0}
                    >
                      <option value="">All Species</option>
                      {getUniqueSpecies().map(species => (
                        <option key={species} value={species}>{species}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="sort-controls">
                  <div className="control-group">
                    <label htmlFor="sort-by">Sort by:</label>
                    <select
                      id="sort-by"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="sort-select"
                    >
                      <option value="">Default</option>
                      <option value="name-asc">Name (A-Z)</option>
                      <option value="name-desc">Name (Z-A)</option>
                      <option value="age-oldest">Age (Oldest First)</option>
                      <option value="age-newest">Age (Newest First)</option>
                    </select>
                  </div>
                </div>

                {(searchQuery || filterCategory || filterSpecies || sortBy) && (
                  <button onClick={clearFilters} className="btn-clear-filters">
                    Clear All
                  </button>
                )}
              </div>
                </>
              )}

              {displayedPlants.length === 0 ? (
                <div className="empty-state">
                  <p>🔍 No plants match your filters</p>
                  <p>Try adjusting your filter or sort options.</p>
                  <button onClick={clearFilters} className="btn btn-primary">
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="plants-grid">
                  {displayedPlants.map((plant) => (
                    <PlantCard 
                      key={plant._id} 
                      plant={plant}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AIPlantLens 
        isOpen={isAILensOpen}
        onClose={() => setIsAILensOpen(false)}
        onAddToInventory={handleAddToInventory}
        onAddToWishlist={handleAddToWishlist}
      />
    </div>
  );
}

export default Dashboard;
